/**
 * i18next bootstrap for the dashboard.
 *
 * Per `aidlc-docs/construction/auth-workspaces-rbac/nfr-design/i18n-strategy.md § 3`.
 *
 * - English is bundled (no http-backend yet — adding a new locale = drop a
 *   sibling folder under `./en/`, register here, update `./types.ts`).
 * - Language detector walks `localStorage` → `navigator` and persists the
 *   chosen locale under `cc.locale`.
 * - Side-effect import: `import "./i18n"` in `main.tsx` BEFORE `<App />`
 *   renders so the singleton is hydrated by first render.
 */

import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import enAgents from "../agents/i18n/agents.json";
import enFeedback from "../feedback/i18n/feedback.json";
import enKnowledge from "../knowledge/i18n/knowledge.json";
import enOnboarding from "../onboarding/i18n/onboarding.json";
import enTables from "../tables/i18n/tables.json";
import enTools from "../tools/i18n/tools.json";
import enWorkflows from "../workflows/i18n/workflows.json";
import enApikeys from "./en/apikeys.json";
import enAuth from "./en/auth.json";
import enCommon from "./en/common.json";
import enContacts from "./en/contacts.json";
import enErrors from "./en/errors.json";
import enMcpAccess from "./en/mcp-access.json";
import enNotifications from "./en/notifications.json";
import enSigningKeys from "./en/signing-keys.json";
import enWorkspace from "./en/workspace.json";

const defaultLocale = (import.meta.env.VITE_DEFAULT_LOCALE as string | undefined) ?? "en";

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: defaultLocale,
    defaultNS: "common",
    ns: [
      "common",
      "auth",
      "workspace",
      "apikeys",
      "errors",
      "agents",
      "contacts",
      "signing-keys",
      "mcp-access",
      "knowledge",
      "tables",
      "tools",
      "feedback",
      "onboarding",
      "notifications",
      "workflows",
    ],
    interpolation: { escapeValue: false },
    resources: {
      en: {
        common: enCommon,
        auth: enAuth,
        workspace: enWorkspace,
        apikeys: enApikeys,
        errors: enErrors,
        agents: enAgents,
        contacts: enContacts,
        "signing-keys": enSigningKeys,
        "mcp-access": enMcpAccess,
        knowledge: enKnowledge,
        tables: enTables,
        tools: enTools,
        feedback: enFeedback,
        onboarding: enOnboarding,
        notifications: enNotifications,
        workflows: enWorkflows,
      },
    },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "cc.locale",
    },
    react: {
      useSuspense: false,
    },
  });

export { i18n };
